import { queryOne } from '../core/dom.js';
import { avatarFor } from '../core/player-identity.js';

const GAME_LABELS = Object.freeze({
  chess: 'Cờ vua',
  xiangqi: 'Cờ tướng',
  caro: 'Cờ ca-rô',
  tycoon: 'Cờ tỷ phú'
});

function create(tag, className = '', text = '') {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  try {
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  } catch {
    return '—';
  }
}

function outcomeLabel(outcome) {
  if (outcome === 'win') return 'Thắng';
  if (outcome === 'draw') return 'Hòa';
  return 'Thua';
}

function signed(value) {
  const n = Number(value || 0);
  return n > 0 ? `+${n}` : String(n);
}

export class CompetitiveSocialController {
  constructor({
    identity,
    baseUrl,
    getRoomContext = () => ({}),
    fetchImpl = (...args) => fetch(...args)
  }) {
    this.identity = identity;
    this.baseUrl = String(baseUrl || '').replace(/\/$/, '');
    this.getRoomContext = getRoomContext;
    this.fetchImpl = fetchImpl;
    this.dashboard = null;
    this.leaderboard = null;
    this.loading = false;
  }

  async api(path, { method = 'GET', body = null, authenticated = true } = {}) {
    if (authenticated) await this.identity.ensureGuest();
    const headers = { Accept: 'application/json' };
    if (body !== null) headers['Content-Type'] = 'application/json';
    if (authenticated && this.identity.token) headers['X-IVS-Player-Token'] = this.identity.token;

    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === null ? undefined : JSON.stringify(body)
    });
    let result = {};
    try { result = await response.json(); } catch {}
    if (!response.ok) {
      const error = new Error(result.error || 'Không thể tải dữ liệu cộng đồng');
      error.status = response.status;
      throw error;
    }
    return result;
  }

  mount() {
    queryOne('#communityBtn')?.addEventListener('click', () => void this.open());
    queryOne('#communityCloseBtn')?.addEventListener('click', () => queryOne('#communityDialog')?.close());
    queryOne('#communityRefreshBtn')?.addEventListener('click', () => void this.refresh());
    queryOne('#communityGameFilter')?.addEventListener('change', () => void this.refreshLeaderboard());
    queryOne('#friendAddForm')?.addEventListener('submit', event => void this.addFriend(event));
    queryOne('#copyFriendCodeBtn')?.addEventListener('click', () => void this.copyFriendCode());
    queryOne('#communityIncomingList')?.addEventListener('click', event => void this.handleIncomingAction(event));
    queryOne('#communityFriendsList')?.addEventListener('click', event => void this.handleFriendAction(event));
    queryOne('#communityInvitesList')?.addEventListener('click', event => void this.handleInviteAction(event));
  }

  setStatus(message = '', isError = false) {
    const el = queryOne('#communityStatus');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('is-error', isError);
  }

  async open() {
    const dialog = queryOne('#communityDialog');
    if (!dialog) return;
    dialog.showModal();
    await this.refresh();
  }

  async refresh() {
    if (this.loading) return;
    this.loading = true;
    const refreshBtn = queryOne('#communityRefreshBtn');
    if (refreshBtn) {
      refreshBtn.disabled = true;
      refreshBtn.textContent = 'Đang tải…';
    }
    this.setStatus('');

    try {
      await this.identity.ensureGuest();
      const gameType = queryOne('#communityGameFilter')?.value || 'chess';
      const [dashboard, leaderboard] = await Promise.all([
        this.api('/api/competitive/me'),
        this.api(`/api/competitive/leaderboard?game=${encodeURIComponent(gameType)}&limit=30`, { authenticated: false })
      ]);
      this.dashboard = dashboard;
      this.leaderboard = leaderboard;
      this.render();
    } catch (error) {
      this.setStatus(error.message || 'Không thể tải dữ liệu cộng đồng.', true);
    } finally {
      this.loading = false;
      if (refreshBtn) {
        refreshBtn.disabled = false;
        refreshBtn.textContent = '↻ Làm mới';
      }
    }
  }

  async refreshLeaderboard() {
    try {
      const gameType = queryOne('#communityGameFilter')?.value || 'chess';
      this.leaderboard = await this.api(
        `/api/competitive/leaderboard?game=${encodeURIComponent(gameType)}&limit=30`,
        { authenticated: false }
      );
      this.renderLeaderboard();
    } catch (error) {
      this.setStatus(error.message || 'Không thể tải bảng xếp hạng.', true);
    }
  }

  render() {
    const profile = this.identity.profile;
    if (profile) {
      const code = queryOne('#communityFriendCode');
      if (code) code.textContent = profile.friendCode || '—';
      const summary = queryOne('#communityIdentitySummary');
      if (summary) {
        const avatar = avatarFor(profile.avatarId);
        summary.textContent = `${avatar.symbol} ${profile.displayName} · ${profile.accountType === 'google' ? 'Tài khoản IVS' : 'Guest'}`;
      }
    }
    this.renderStats();
    this.renderLeaderboard();
    this.renderHistory();
    this.renderSocial();
  }

  renderStats() {
    const root = queryOne('#communityStatsGrid');
    if (!root || !this.dashboard?.stats) return;
    root.innerHTML = '';
    for (const gameType of Object.keys(GAME_LABELS)) {
      const stats = this.dashboard.stats[gameType];
      const card = create('article', 'community-stat-card');
      const title = create('span', 'community-stat-game', GAME_LABELS[gameType]);
      const rating = create('strong', 'community-stat-rating', String(stats?.rating ?? 1200));
      const record = create(
        'small',
        'community-stat-record',
        `${stats?.wins || 0}T · ${stats?.draws || 0}H · ${stats?.losses || 0}B · ${stats?.games || 0} trận`
      );
      card.append(title, rating, record);
      root.append(card);
    }

    const totals = this.dashboard.totals || {};
    const totalEl = queryOne('#communityTotalRecord');
    if (totalEl) {
      totalEl.textContent = `${totals.games || 0} trận · ${totals.wins || 0} thắng · ${totals.draws || 0} hòa · ${totals.losses || 0} thua`;
    }
  }

  renderLeaderboard() {
    const root = queryOne('#communityLeaderboardList');
    if (!root) return;
    root.innerHTML = '';
    const entries = this.leaderboard?.entries || [];
    if (!entries.length) {
      root.append(create('div', 'community-empty', 'Chưa có trận LIVE được xếp hạng.'));
      return;
    }

    for (const entry of entries) {
      const row = create('div', 'community-rank-row');
      const rank = create('strong', 'community-rank-number', `#${entry.rank}`);
      const identity = create('div', 'community-rank-player');
      const name = create('strong', '', `${avatarFor(entry.avatarId).symbol} ${entry.displayName}`);
      const record = create('small', '', `${entry.wins || 0}T · ${entry.draws || 0}H · ${entry.losses || 0}B`);
      identity.append(name, record);
      const rating = create('strong', 'community-rank-rating', String(entry.rating));
      row.append(rank, identity, rating);
      root.append(row);
    }
  }

  renderHistory() {
    const root = queryOne('#communityHistoryList');
    if (!root) return;
    root.innerHTML = '';
    const history = this.dashboard?.history || [];
    if (!history.length) {
      root.append(create('div', 'community-empty', 'Chưa có lịch sử trận LIVE.'));
      return;
    }

    for (const match of history.slice(0, 20)) {
      const row = create('article', `community-history-row outcome-${match.outcome}`);
      const head = create('div', 'community-history-head');
      head.append(
        create('strong', '', `${GAME_LABELS[match.gameType] || match.gameType} · ${outcomeLabel(match.outcome)}`),
        create('span', 'community-rating-delta', `${signed(match.ratingDelta)} ELO`)
      );
      const meta = create('small', '', `Phòng ${match.roomCode || '—'} · ${formatDate(match.endedAt)}`);
      const reason = create('small', 'community-history-reason', match.reason || '');
      row.append(head, meta, reason);
      root.append(row);
    }
  }

  renderSocial() {
    const social = this.dashboard?.social || {};
    this.renderIncoming(social.incoming || [], social.outgoing || []);
    this.renderFriends(social.friends || []);
    this.renderInvites(social.invites || []);
  }

  renderIncoming(incoming, outgoing) {
    const root = queryOne('#communityIncomingList');
    if (!root) return;
    root.innerHTML = '';

    if (!incoming.length && !outgoing.length) {
      root.append(create('div', 'community-empty', 'Không có lời mời kết bạn đang chờ.'));
      return;
    }

    for (const request of incoming) {
      const row = create('div', 'community-social-row');
      const info = create('div', 'community-social-person');
      info.append(
        create('strong', '', `${avatarFor(request.avatarId).symbol} ${request.displayName}`),
        create('small', '', 'Muốn kết bạn với bạn')
      );
      const actions = create('div', 'community-inline-actions');
      const accept = create('button', 'sub-btn compact', 'Chấp nhận');
      accept.type = 'button';
      accept.dataset.friendRequest = request.requestId;
      accept.dataset.accept = 'true';
      const reject = create('button', 'text-btn compact', 'Từ chối');
      reject.type = 'button';
      reject.dataset.friendRequest = request.requestId;
      reject.dataset.accept = 'false';
      actions.append(accept, reject);
      row.append(info, actions);
      root.append(row);
    }

    for (const request of outgoing) {
      const row = create('div', 'community-social-row is-muted');
      const info = create('div', 'community-social-person');
      info.append(
        create('strong', '', `${avatarFor(request.avatarId).symbol} ${request.displayName}`),
        create('small', '', 'Đã gửi lời mời · đang chờ phản hồi')
      );
      row.append(info);
      root.append(row);
    }
  }

  renderFriends(friends) {
    const root = queryOne('#communityFriendsList');
    if (!root) return;
    root.innerHTML = '';
    if (!friends.length) {
      root.append(create('div', 'community-empty', 'Chưa có bạn bè. Chia sẻ mã kết bạn ở phía trên.'));
      return;
    }

    const room = this.getRoomContext() || {};
    for (const friend of friends) {
      const row = create('div', 'community-social-row');
      const info = create('div', 'community-social-person');
      info.append(
        create('strong', '', `${avatarFor(friend.avatarId).symbol} ${friend.displayName}`),
        create('small', '', `Bạn bè từ ${formatDate(friend.since)}`)
      );

      const actions = create('div', 'community-inline-actions');
      const invite = create('button', 'sub-btn compact', room.roomCode ? 'Mời vào phòng' : 'Chưa có phòng');
      invite.type = 'button';
      invite.disabled = !room.roomCode;
      invite.dataset.invitePlayer = friend.playerId;

      const remove = create('button', 'text-btn compact', 'Xóa');
      remove.type = 'button';
      remove.dataset.removeFriend = friend.playerId;
      actions.append(invite, remove);
      row.append(info, actions);
      root.append(row);
    }
  }

  renderInvites(invites) {
    const root = queryOne('#communityInvitesList');
    if (!root) return;
    root.innerHTML = '';
    if (!invites.length) {
      root.append(create('div', 'community-empty', 'Chưa có lời mời vào phòng.'));
      return;
    }

    for (const invite of invites) {
      const row = create('div', 'community-social-row');
      const info = create('div', 'community-social-person');
      const lock = invite.hasPassword ? ' · Có mật khẩu' : '';
      info.append(
        create('strong', '', `${avatarFor(invite.from?.avatarId).symbol} ${invite.from?.displayName || 'Bạn bè'}`),
        create('small', '', `${GAME_LABELS[invite.gameType] || invite.gameType} · Phòng ${invite.roomCode}${lock}`)
      );
      const actions = create('div', 'community-inline-actions');
      const join = create('button', 'sub-btn compact', 'Vào phòng');
      join.type = 'button';
      join.dataset.joinInvite = invite.inviteId;
      join.dataset.game = invite.gameType;
      join.dataset.room = invite.roomCode;
      const dismiss = create('button', 'text-btn compact', 'Bỏ qua');
      dismiss.type = 'button';
      dismiss.dataset.dismissInvite = invite.inviteId;
      actions.append(join, dismiss);
      row.append(info, actions);
      root.append(row);
    }
  }

  async addFriend(event) {
    event.preventDefault();
    const input = queryOne('#friendCodeInput');
    const code = String(input?.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 12);
    if (!code) return this.setStatus('Nhập mã kết bạn trước.', true);

    try {
      await this.api('/api/social/friends/request', {
        method: 'POST',
        body: { friendCode: code }
      });
      if (input) input.value = '';
      this.setStatus('Đã gửi lời mời kết bạn.');
      await this.refresh();
    } catch (error) {
      this.setStatus(error.message || 'Không thể gửi lời mời kết bạn.', true);
    }
  }

  async handleIncomingAction(event) {
    const button = event.target.closest('[data-friend-request]');
    if (!button) return;
    try {
      await this.api('/api/social/friends/respond', {
        method: 'POST',
        body: {
          requestId: button.dataset.friendRequest,
          accept: button.dataset.accept === 'true'
        }
      });
      await this.refresh();
    } catch (error) {
      this.setStatus(error.message || 'Không thể xử lý lời mời.', true);
    }
  }

  async handleFriendAction(event) {
    const invite = event.target.closest('[data-invite-player]');
    const remove = event.target.closest('[data-remove-friend]');

    if (invite) {
      const room = this.getRoomContext() || {};
      if (!room.roomCode || !room.gameType) {
        this.setStatus('Hãy tạo hoặc vào một phòng LIVE trước khi mời bạn.', true);
        return;
      }
      try {
        await this.api('/api/social/invites', {
          method: 'POST',
          body: {
            targetPlayerId: invite.dataset.invitePlayer,
            gameType: room.gameType,
            roomCode: room.roomCode,
            hasPassword: room.hasPassword === true
          }
        });
        this.setStatus('Đã gửi lời mời vào phòng.');
      } catch (error) {
        this.setStatus(error.message || 'Không thể gửi lời mời vào phòng.', true);
      }
      return;
    }

    if (remove) {
      try {
        await this.api(`/api/social/friends/${encodeURIComponent(remove.dataset.removeFriend)}`, {
          method: 'DELETE'
        });
        await this.refresh();
      } catch (error) {
        this.setStatus(error.message || 'Không thể xóa bạn bè.', true);
      }
    }
  }

  async handleInviteAction(event) {
    const join = event.target.closest('[data-join-invite]');
    const dismiss = event.target.closest('[data-dismiss-invite]');
    const button = join || dismiss;
    if (!button) return;

    const inviteId = join?.dataset.joinInvite || dismiss?.dataset.dismissInvite;
    try {
      await this.api('/api/social/invites/respond', {
        method: 'POST',
        body: { inviteId }
      });
      if (join) {
        const url = new URL(globalThis.location.href);
        url.search = '';
        url.searchParams.set('game', join.dataset.game);
        url.searchParams.set('room', join.dataset.room);
        globalThis.location.assign(url.toString());
        return;
      }
      await this.refresh();
    } catch (error) {
      this.setStatus(error.message || 'Không thể xử lý lời mời vào phòng.', true);
    }
  }

  async copyFriendCode() {
    const code = this.identity.profile?.friendCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.setStatus('Đã sao chép mã kết bạn.');
    } catch {
      this.setStatus(`Mã kết bạn: ${code}`);
    }
  }
}
